<template>
	<div
		v-if="renderComponent"
		class="resources"
		data-cy="resources"
	>
		<helio-field
			v-if="search"
			:placeholder="placeholder"
			:display-success="false"
			icon="search"
			icon-weight="light"
			icon-position="left"
			class="resources__search"
			data-cy="resources-search"
			@changed="searchTerm = $event"
		/>

		<div
			v-if="sortedResources.length > 0"
			ref="resources-list"
			:class="{
				'scroll': scrollable,
			}"
			class="resources__list"
		>
			<div
				v-for="(resource, index) in sortedResources"
				:key="index"
				:class="{
					'resource--active' : resource.active
				}"
				data-cy="resource"
				class="resource"
				@click="$emit('click', resource)"
				@keydown.space="$emit('click', resource)"
			>
				<div
					v-if="resource.letter && alphabetical"
					class="resource__separator"
					data-cy="resource-separator"
				>
					{{ resource.letter }}
				</div>

				<div
					:class="{
						'resource__content--one-line': (!resource[subtitleProperty])
					}"
					class="resource__content"
					tabindex="0"
					role="button"
				>
					<div
						v-if="imageProperty"
						class="resource__avatar"
					>
						<helio-avatar
							:animation="true"
							:user="{
								image: resource[imageProperty]
							}"
							:badge="resource[badgeProperty] ? badgeDefaultData : null"
						/>
					</div>

					<div class="resource__informations">
						<div
							v-if="resource[titleProperty]"
							class="resource__informations--top"
						>
							<helio-icon
								v-if="renderIcon && resource[iconProperty]"
								:token="resource[iconProperty]"
								class="resource__icon"
								data-cy="resource-icon"
							/>

							<h4
								v-if="resource[titleProperty]"
								class="resource__title"
								data-cy="resource-title"
							>
								{{ resource[titleProperty] }}
							</h4>

							<div
								class="resource__extra"
								data-cy="resource-extra"
							>
								<slot
									:resource="resource"
									name="extra"
								/>
							</div>
						</div>

						<div
							v-if="resource[subtitleProperty]"
							class="resource__informations--bottom"
						>
							<p
								v-if="resource[subtitleProperty]"
								class="resource__subtitle"
								data-cy="resource-subtitle"
							>
								{{ resource[subtitleProperty] }}
							</p>
						</div>
					</div>

					<div
						class="resource__append"
						data-cy="resource-append"
					>
						<slot
							name="append"
							:resource="resource"
						/>
					</div>
				</div>
			</div>
		</div>
		<div
			v-if="sortedResources.length === 0"
			class="resources__empty"
		>
			<helio-empty
				:initial-title="$t('resources.empty.title')"
				:initial-subtitle="emptySubtitle"
				illustration="feather"
			/>
		</div>
	</div>
</template>

<script>
import Fuse from 'fuse.js';
import Icon from '../icon/Icon.vue';
import Avatar from '../avatar/Avatar.vue';
import Empty from '../empty/Empty.vue';
import Field from '../field/Field.vue';

export default {
	name: 'Resources',

	components: {
		'helio-icon': Icon,
		'helio-avatar': Avatar,
		'helio-empty': Empty,
		'helio-field': Field,
	},

	props: {
		alphabetical: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the resources within the Resources component will be shown in alphabetical order',
		},

		badgeProperty: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether badges will appear on resources\' avatars (assuming the imageProperty prop is truthy)',
		},

		iconProperty: {
			type: String,
			required: false,
			default: '',
			description: 'Sets whether icons will appear next to resource labels',
		},

		imageProperty: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether resources\' avatars will appear on the left-hand side of the entry',
		},

		placeholder: {
			type: String,
			required: false,
			default: 'Search..',
			description: 'Sets the placeholder text in the Resources component search field',
		},

		resources: {
			type: Array,
			required: true,
			description: 'Supplies information about each resource in the Resources component',
		},

		search: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Resources component will be searchable via a search field',
		},

		subtitleProperty: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether resources\' subtitles will appear',
		},

		titleProperty: {
			type: String,
			required: true,
			description: 'Sets whether resources\' titles will appear',
		},
	},

	slots: {
		append: {
			required: false,
			scoped: true,
			description: 'Supplies content to the right-hand area of resource entries',
		},

		extra: {
			required: false,
			scoped: true,
			description: 'Supplies additional content to resources between the title area on the left and the append area on the right',
		},
	},

	data() {
		return {
			searchTerm: null,
			selection: [],
			fuse: null,
			scrollable: false,
			renderIcon: true,
			renderComponent: false,
			options: {
				shouldSort: true,
				threshold: 0.3,
				location: 0,
				distance: 100,
				maxPatternLength: 32,
				minMatchCharLength: 1,
				keys: [
					{
						name: this.titleProperty,
						weight: 0.7,
					},
					{
						name: this.subtitleProperty,
						weight: 0.3,
					},
				],
			},
			badgeDefaultData: {
				token: 'dollar-sign',
				weight: 'regular',
				position: 'top-right',
			},
		};
	},

	computed: {
		sortedResources() {
			if (this.alphabetical) {
				// eslint-disable-next-line
				const resources = this.selection.sort((a, b) => {
					if (a.name > b.name) {
						return 1;
					}

					if (b.name > a.name) {
						return -1;
					}

					return 0;
				});

				let firstLetter;
				resources.map((resource) => {
					const newFirstLetter = resource[this.titleProperty].charAt(0).replace('É', 'E');

					if (firstLetter !== newFirstLetter) {
						resource.letter = newFirstLetter;
						firstLetter = newFirstLetter;
					} else {
						delete resource.letter;
					}

					return resource;
				});

				return resources;
			}

			return this.selection;
		},

		emptySubtitle() {
			if (this.searchTerm && this.searchTerm !== '') {
				return this.$t('resources.empty.subtitle.search', [this.searchTerm]);
			}

			return this.$t('resources.empty.subtitle.default');
		},
	},

	watch: {
		sortedResources() {
			setTimeout(() => {
				this.checkIfScrollable();
			}, 10);
		},

		searchTerm(term) {
			if (term !== '') {
				this.selection = this.fuse.search(term);
			} else {
				this.selection = this.resources;
			}
		},

		resources: {
			// eslint-disable-next-line
			handler: function () {
				this.setupFuse(this.resources);
				this.renderIcon = false;

				if (this.searchTerm && this.searchTerm !== '') {
					this.selection = this.fuse.search(this.searchTerm);
				}
			},
			deep: true,
		},

		scrollable() {
			this.$forceUpdate();
		},

		renderIcon() {
			if (!this.renderIcon) {
				this.$nextTick(() => {
					this.renderIcon = true;
				});
			}
		},
	},

	created() {
		this.setupFuse(this.resources);
	},

	mounted() {
		this.checkIfScrollable();
		window.addEventListener('resize', this.checkIfScrollable);
		window.addEventListener('keydown', this.checkForSpace);

		setTimeout(() => {
			this.renderComponent = true;
		}, 10);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.checkIfScrollable);
		window.removeEventListener('keydown', this.checkForSpace);
	},

	methods: {
		setupFuse() {
			this.selection = this.resources;
			this.fuse = new Fuse(this.selection, this.options);
		},

		checkIfScrollable() {
			const list = this.$refs['resources-list'];

			if (list) {
				this.scrollable = (list.scrollHeight > list.clientHeight);
			}
		},

		checkForSpace(event) {
			const { keyCode } = event;
			const currentElement = document.activeElement;

			if (keyCode === 32 && currentElement && currentElement.classList.contains('resource__content')) {
				this.$emit('click', this.resource);
			}
		},
	},
};
</script>

<style lang="scss" src="./resources.scss" />
